import React, { useState, useMemo, useCallback } from 'react'
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap'
import { deleteMembershipWithReassign } from '../../api'

/**
 * Confirms user removal and handles reassignment of:
 * - companies.contact_id  (when the removed user is the company contact)
 * - jobs.contact (NCS cnt_id) on open/pending jobs in the same company
 * - TODO?: job_user pivot rows (silently removed)
 *
 * Props:
 *   membership          – the membership row being removed
 *   companyContactId    – current companies.contact_id (from getUsers() response)
 *   userList            – all membership rows for the company (from getUsers() response)
 *   onSuccess(payload)  – called with { users, contact_id } on successful delete (same shape as GET /users)
 *   onCancel()          – called when the modal is dismissed
 */
const RemoveUserModal = ({ membership, companyContactId, userList, onSuccess, onCancel }) => {
	const [newContactId, setNewContactId] = useState('')
	const [isRemoving, setIsRemoving] = useState(false)
	const [error, setError] = useState('')

	const isCompanyContact = companyContactId === membership.user_id
	const otherMembers = useMemo(
		() => userList.filter(m => m.id !== membership.id),
		[userList, membership.id]
	)
	const canConfirm = !isCompanyContact || newContactId !== ''

	const selectedUser = useMemo(() => {
		if (!newContactId) return null
		return otherMembers.find(m => m.user_id === Number(newContactId))
	}, [otherMembers, newContactId])

	const handleConfirm = useCallback(() => {
		setIsRemoving(true)
		setError('')
		deleteMembershipWithReassign(membership.id, newContactId || null)
			.then(payload => onSuccess(payload))
			.catch(() => setError('An error occurred. Please try again.'))
			.finally(() => setIsRemoving(false))
	}, [membership.id, newContactId, onSuccess])

	const handleNewContactChange = useCallback(e => {
		setNewContactId(e.target.value)
	}, [])

	return (
		<Modal isOpen toggle={onCancel}>
			<ModalHeader toggle={onCancel}>
				Remove User: {membership.user.name}
			</ModalHeader>
			<ModalBody>
				<p>
					You are about to remove <strong>{membership.user.name}</strong> from the company.
					Their account will be preserved but they will no longer have access.
				</p>

				{isCompanyContact && (
					<div className="form-group mt-3">
						<label htmlFor="new-contact-select">
							<strong>This user is the company contact.</strong>
							<br />
							<span className="text-muted">Select a replacement contact before removing:</span>
						</label>
						<select
							id="new-contact-select"
							className="form-control mt-1"
							value={newContactId}
							onChange={handleNewContactChange}
						>
							<option value="">— Select new company contact —</option>
							{otherMembers.map(m => (
								<option key={m.id} value={m.user_id}>
									{m.user.name}
									{m.user.cnt_id ? ` (CNT: ${m.user.cnt_id})` : ' (no CNT ID)'}
								</option>
							))}
						</select>
						{selectedUser && !selectedUser.user.cnt_id && (
							<small className="text-warning mt-1 d-block">
								<i className="fas fa-exclamation-triangle mr-1"></i>
								The selected user has no CNT ID — open/pending jobs with this user as contact will not be updated.
							</small>
						)}
					</div>
				)}

				{error && <p className="text-danger mt-2">{error}</p>}
			</ModalBody>
			<ModalFooter>
				<Button
					color="danger"
					onClick={handleConfirm}
					disabled={isRemoving || !canConfirm}
				>
					{isRemoving ? 'Removing...' : 'Remove User'}
				</Button>
				<Button color="secondary" onClick={onCancel} disabled={isRemoving}>
					Cancel
				</Button>
			</ModalFooter>
		</Modal>
	)
}

export default RemoveUserModal;
